import { POST_TYPE_CONFIG, DATA_MAPPING } from './FilterConfig';

export class FilterDataManager {
  constructor( apiData ) {
    this.apiData = apiData;
    this.sortedData = this.initializeSortedData();
  }

  initializeSortedData() {
    return {
      ageRange: this.sortAlphabetically( this.apiData[DATA_MAPPING['age-range']] || [] ),
      category: this.sortAlphabetically( this.apiData[DATA_MAPPING.category] || [] ),
      brand: this.sortAlphabetically( this.apiData[DATA_MAPPING.brand] || [] ),
      imprint: this.sortAlphabetically( this.apiData[DATA_MAPPING.imprint] || [] ),
      series: this.sortAlphabetically( this.apiData[DATA_MAPPING.series] || [] ),
      theme: this.sortAlphabetically( this.apiData[DATA_MAPPING.theme] || [] ),
      collection: this.sortAlphabetically( this.apiData[DATA_MAPPING.collection] || [] )
    };
  }

  checkForNumericValue( value ) {
    if ( !value ) return value;
    let start = 0;
    let end = 1;
    let numericValue = '';
    while ( !isNaN( parseInt( value.substring( start, end ) ) ) ) {
      numericValue += value.substring( start, end );
      start++;
      end++;
    }
    if ( !numericValue ) return value;
    return parseInt( numericValue );
  }

  sortAlphabetically( data ) {
    return data.sort( ( a, b ) => {
      let aValue = this.checkForNumericValue( a.title?.rendered );
      let bValue = this.checkForNumericValue( b.title?.rendered );

      if ( aValue < bValue ) return -1;
      if ( aValue > bValue ) return 1;
      return 0;
    });
  }

  getAssociatedData( postType, postId ) {
    const config = POST_TYPE_CONFIG[postType];
    if ( !config ) return this.getAllData();

    // Handle top-level books page (no postId needed)
    if ( postType === 'books' ) {
      return this.getAllData();
    }

    // For other post types, we need a postId
    if ( !postId ) return this.getAllData();

    const currentPost = this.findPostById( postType, postId );
    if ( !currentPost ) return this.getAllData();

    const result = {};
    
    // For each data type, check if it should be included based on associations
    Object.keys( this.sortedData ).forEach( dataType => {
      const associationKey = this.getAssociationKey( dataType );
      
      if ( config.associations.includes( associationKey ) ) {
        result[dataType] = this.getFilteredAssociations( currentPost, associationKey, dataType );
      } else {
        result[dataType] = [];
      }
    });

    return result;
  }

  findPostById( postType, postId ) {
    const dataIndex = this.getDataIndexForPostType( postType );
    if ( dataIndex === -1 ) return null;
    
    return this.apiData[dataIndex]?.find( item => item.id.toString() === postId );
  }

  getDataIndexForPostType( postType ) {
    const mapping = {
      'age-range': DATA_MAPPING['age-range'],
      'brand': DATA_MAPPING.brand,
      'book-category': DATA_MAPPING.category,
      'imprint': DATA_MAPPING.imprint,
      'series': DATA_MAPPING.series,
      'book-theme': DATA_MAPPING.theme
    };
    return mapping[postType] ?? -1;
  }

  getAssociationKey( dataType ) {
    const mapping = {
      ageRange: 'age_range',
      category: 'book_category',
      brand: 'brand',
      imprint: 'imprint',
      series: 'series',
      theme: 'book_theme'
    };
    return mapping[dataType] || dataType;
  }

  getFilteredAssociations( currentPost, associationKey, dataType ) {
    const associatedIds = currentPost.ACF?.[associationKey];
    if ( !associatedIds || !Array.isArray( associatedIds ) ) return [];

    return this.sortedData[dataType].filter( item => 
      associatedIds.includes( item.id )
    );
  }

  getAllData() {
    return this.sortedData;
  }

  shouldShowNavigation( postType, data ) {
    const config = POST_TYPE_CONFIG[postType];
    if ( !config ) return false;

    // Check if browse by is enabled for this post type
    if ( !config.browseByAtTopLevel ) return false;

    if ( postType === 'books' ) return true;
    
    // For post type pages, only show nav if there are associated items
    return Object.values( data ).some( arr => arr.length > 0 );
  }

  getAvailableFilters( postType ) {
    const config = POST_TYPE_CONFIG[postType] || POST_TYPE_CONFIG.books;
    return config.availableFilters || [];
  }

  shouldShowFilter( postType, filterType, hasData = false ) {
    const config = POST_TYPE_CONFIG[postType] || POST_TYPE_CONFIG.books;
    
    // Check if filters are disabled for this post type
    if ( config.showFilters === false ) return false;
    
    // Check if this filter is in the available list
    if ( !config.availableFilters.includes( filterType ) ) return false;
    
    // For association-based filters, only show if there's data
    const associationBasedFilters = [ 'age-range', 'brand', 'category', 'imprint', 'series', 'theme' ];
    if ( associationBasedFilters.includes( filterType ) ) {
      return hasData;
    }
    
    return true;
  }
}
